// components/ui/Switch.tsx
import React, { memo, useCallback } from 'react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from './tooltip';

type SwitchProps = {
  checked: boolean;
  onChange?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onCheckedChange?: (checked: boolean) => void;
  labelLeft?: React.ReactNode;
  labelRight: React.ReactNode;
  tooltip?: string;
  disabled?: boolean;
  ariaLabel?: string;
};

const Switch: React.FC<SwitchProps> = memo(({
  checked,
  onChange,
  onCheckedChange,
  labelLeft,
  labelRight,
  tooltip,
  disabled = false,
  ariaLabel
}) => {
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (onCheckedChange) {
      onCheckedChange(!checked);
    } else if (onChange) {
      onChange(e);
    }
  }, [checked, onChange, onCheckedChange, disabled]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    if (onCheckedChange) onCheckedChange(!checked);
    else if (onChange) onChange(event as unknown as React.MouseEvent<HTMLDivElement>);
  }, [checked, onChange, onCheckedChange, disabled]);

  const renderLabel = (content: React.ReactNode, isActive: boolean) => {
    if (!content) return null;
    return (
      <span className={`text-[12px] transition-colors ${isActive ? 'text-white/75' : 'text-white/35'}`}>
        {content}
      </span>
    );
  };

  return (
    <label className={`flex items-center gap-1.5 ${disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer'}`}>
      {renderLabel(labelLeft, !checked)}
      
      <Tooltip>
        {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
        <TooltipTrigger asChild>
          <div
            role="switch"
            aria-label={ariaLabel}
            aria-checked={checked}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={`relative h-[18px] w-8 shrink-0 rounded-full border transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6]/70 ${
              checked ? 'border-[#3B82F6]/60 bg-[#3B82F6]' : 'border-[#2A2A2A] bg-white/20'
            }`}
          >
            <span className={`absolute left-[2px] top-[2px] h-3 w-3 rounded-full bg-white shadow-sm transition-transform duration-150 ${checked ? 'translate-x-[14px]' : 'translate-x-0'}`} />
          </div>
        </TooltipTrigger>
      </Tooltip>

      {renderLabel(labelRight, checked)}
    </label>
  );
});

Switch.displayName = "Switch";

export default Switch;
